import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { SortOrder } from '@/types/common'
import { TABLE_PAGE_SIZE } from '@/utils/constants'

import { Project, ProjectFilters } from './types'

interface ProjectStore {
  searchText: string
  filters: ProjectFilters
  page: number
  rowsPerPage: number
  sort: {
    column: keyof Project
    direction: SortOrder
  }
  editingProject: Project | null
  dialogOpen: boolean
  deletingProject: Project | null
  deleteDialogOpen: boolean
  contextCompanyId: string | null

  // Actions
  setSearchText: (searchText: string) => void
  setFilter: (payload: Partial<ProjectFilters>) => void
  setPage: (page: number) => void
  setRowsPerPage: (rowsPerPage: number) => void
  setSort: (sort: {
    column: keyof Project | null
    direction: SortOrder | null
  }) => void
  setEditingProject: (item: Project | null) => void
  clearEditingProject: () => void
  setDialogOpen: (open: boolean, companyId?: string) => void
  setDeletingProject: (item: Project | null) => void
  clearDeletingProject: () => void
  setDeleteDialogOpen: (open: boolean) => void
  resetFilters: () => void
  reset: () => void
}

const initialState = {
  searchText: '',
  filters: {
    searchName: '',
    companyName: null,
    companyId: null,
  },
  page: 0,
  rowsPerPage: TABLE_PAGE_SIZE,
  sort: {
    column: 'created_at' as keyof Project,
    direction: 'desc' as SortOrder,
  },
  editingProject: null as Project | null,
  dialogOpen: false,
  deletingProject: null as Project | null,
  deleteDialogOpen: false,
  contextCompanyId: null as string | null,
}

export const useProjectsStore = create<ProjectStore>()(
  devtools(
    (set) => ({
      ...initialState,

      /**
       * Set search text
       */
      setSearchText: (searchText: string) => {
        set({ searchText }, false, 'setSearchText')
      },

      /**
       * Set a filter value
       */
      setFilter: (payload: Partial<ProjectFilters>) => {
        set(
          (state) => ({
            filters: { ...state.filters, ...payload },
            page: 0,
          }),
          false,
          'setFilter'
        )
      },

      /**
       * Set current page
       */
      setPage: (page: number) => {
        set({ page }, false, 'setPage')
      },

      /**
       * Set rows per page
       */
      setRowsPerPage: (rowsPerPage: number) => {
        set({ rowsPerPage, page: 0 }, false, 'setRowsPerPage')
      },

      /**
       * Set sort column and direction
       * If column is null, resets to default sort (created_at desc)
       */
      setSort: (sort: {
        column: keyof Project | null
        direction: SortOrder | null
      }) => {
        if (sort.column === null || sort.direction === null) {
          // Reset to default sort
          set(
            {
              sort: {
                column: 'created_at' as keyof Project,
                direction: 'desc' as SortOrder,
              },
            },
            false,
            'setSort (reset to default)'
          )
        } else {
          set(
            {
              sort: {
                column: sort.column,
                direction: sort.direction,
              },
            },
            false,
            'setSort'
          )
        }
      },

      setEditingProject: (item: Project | null) => {
        set(
          { editingProject: item, dialogOpen: true },
          false,
          'setEditingProject'
        )
      },

      clearEditingProject: () => {
        set({ editingProject: null }, false, 'clearEditingProject')
      },

      setDialogOpen: (open: boolean, companyId?: string) => {
        set(
          {
            dialogOpen: open,
            contextCompanyId: open ? companyId || null : null,
          },
          false,
          'setDialogOpen'
        )
      },

      setDeletingProject: (item: Project | null) => {
        set(
          { deletingProject: item, deleteDialogOpen: true },
          false,
          'setDeletingProject'
        )
      },

      /**
       * Clear the project being deleted
       */
      clearDeletingProject: () => {
        set({ deletingProject: null }, false, 'clearDeletingProject')
      },

      /**
       * Set delete dialog open state
       */
      setDeleteDialogOpen: (open: boolean) => {
        set({ deleteDialogOpen: open }, false, 'setDeleteDialogOpen')
      },

      /**
       * Reset filters to initial state
       */
      resetFilters: () => {
        set(
          {
            filters: initialState.filters,
            page: 0,
          },
          false,
          'resetFilters'
        )
      },

      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    { name: 'ProjectStore' }
  )
)
